/**
 * F-148 §6 corrections (AC23–AC26). Reduce-only against one receipt line: units in
 * `1 ≤ units ≤ correctableUnits`, a required reason (`other` also needs a note) and a
 * reclassification. Increases use a new linked receipt (UI hint).
 *
 * Session lifecycle (AC26): every per-session value — the idempotency key, quantities, reason,
 * note and the "submitted" flag — lives in `CorrectionForm`, which is mounted only while the
 * dialog is open for a line and is keyed by `line.id`. The key is created synchronously by a
 * `useState` lazy initializer, so it exists in the form's first render (before any submit can
 * fire) and no effect ever writes it afterwards. Retries while the dialog stays open reuse the
 * same key; closing, reopening, or switching lines remounts the form and yields a fresh key plus
 * a clean form. See `.dukanti/research/merchant-operations/f148-correction-token-fix.md`.
 */
import { useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import {
  RECEIVING_LIMITS,
  cleanFreeText,
  type CorrectReceiptLineInput,
  type CorrectionReason,
  type StockReceiptLine,
} from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { createIdempotencyKey } from '../lib/idempotency-store';
import { receivingErrorKey, receivingErrorOptions } from '../lib/error-messages';
import { QuantityInput } from './line-quantity-grid';

const REASONS: readonly CorrectionReason[] = [
  'accepted_count_error',
  'damage_found_after_posting',
  'wrong_variant_received',
  'other',
];

export interface CorrectionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly line: StockReceiptLine | null;
  readonly onSubmit: (input: CorrectReceiptLineInput) => Promise<void>;
  readonly isSubmitting?: boolean;
  readonly error?: unknown;
  readonly createKey?: () => string;
}

export function CorrectionDialog({
  open,
  onOpenChange,
  line,
  onSubmit,
  isSubmitting = false,
  error = null,
  createKey = createIdempotencyKey,
}: CorrectionDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => (isSubmitting ? undefined : onOpenChange(next))}>
      <DialogContent
        dir={dir}
        // Bounded height + inner scroll keep Confirm/Cancel reachable at 375x667 with a keyboard.
        className="max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto"
        data-testid="correction-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('admin.receiving.correction.title')}</DialogTitle>
          <DialogDescription>{t('admin.receiving.correction.description')}</DialogDescription>
        </DialogHeader>

        {/* Mount gating + `key` define the session: a new form (and key) per open/line. */}
        {open && line ? (
          <CorrectionForm
            key={line.id}
            line={line}
            onOpenChange={onOpenChange}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            error={error}
            createKey={createKey}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface CorrectionFormProps {
  readonly line: StockReceiptLine;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (input: CorrectReceiptLineInput) => Promise<void>;
  readonly isSubmitting: boolean;
  readonly error: unknown;
  readonly createKey: () => string;
}

function CorrectionForm({
  line,
  onOpenChange,
  onSubmit,
  isSubmitting,
  error,
  createKey,
}: CorrectionFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  // Lazy initializer: the key is fixed for the lifetime of this mount (one open session for one
  // line). Nothing else may set it, so a retry after a failure always resends the same key.
  const [idempotencyKey] = useState<string>(createKey);
  const [units, setUnits] = useState<number | null>(null);
  const [reason, setReason] = useState<CorrectionReason | null>(null);
  const [reclassifyAs, setReclassifyAs] = useState<'missing' | 'damaged'>('damaged');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const max = line.correctableUnits;
  const unitsValid = units !== null && Number.isInteger(units) && units >= 1 && units <= max;
  const noteClean = cleanFreeText(note, RECEIVING_LIMITS.maxNote);
  const noteValue = noteClean.ok ? noteClean.value : null;
  const noteRequiredMissing = reason === 'other' && noteValue === null;
  const formValid = unitsValid && reason !== null && !noteRequiredMissing && noteClean.ok;
  const hasError = error !== null && error !== undefined;

  const submit = async (): Promise<void> => {
    setSubmitted(true);
    if (!formValid || units === null || reason === null) return;
    await onSubmit({
      receiptLineId: line.id,
      units,
      reason,
      reclassifyAs,
      note: noteValue,
      idempotencyKey,
    });
  };

  return (
    <form
      className="space-y-4 text-start"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <div className="border-border bg-admin-surface-muted rounded-lg border p-3 text-sm">
        <p className="text-foreground font-medium">
          <bdi>{line.productNameSnapshot}</bdi>
          {line.optionSummarySnapshot ? (
            <>
              {' — '}
              <bdi>{line.optionSummarySnapshot}</bdi>
            </>
          ) : null}
        </p>
        <p className="text-muted-foreground mt-1 text-xs" data-testid="correction-remaining">
          {t('admin.receiving.correction.remaining', { count: line.correctableUnits })}
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${id}-units`} className="text-sm">
          {t('admin.receiving.correction.units')}
        </Label>
        <QuantityInput
          id={`${id}-units`}
          value={units}
          onChange={setUnits}
          disabled={isSubmitting}
          invalid={submitted && !unitsValid}
          describedBy={submitted && !unitsValid ? `${id}-units-error` : undefined}
          testId="correction-units"
        />
        {submitted && !unitsValid ? (
          <p id={`${id}-units-error`} className="text-destructive text-xs" role="alert">
            {t('admin.receiving.correction.unitsInvalid', { max })}
          </p>
        ) : null}
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('admin.receiving.correction.reason')}</legend>
        <div role="radiogroup" aria-required="true" className="grid gap-2">
          {REASONS.map((candidate) => {
            const radioId = `${id}-reason-${candidate}`;
            return (
              <label
                key={candidate}
                htmlFor={radioId}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm',
                  reason === candidate
                    ? 'border-admin-lime bg-admin-lime-light/40'
                    : 'border-input bg-card',
                )}
              >
                <input
                  id={radioId}
                  type="radio"
                  name={`${id}-reason`}
                  value={candidate}
                  checked={reason === candidate}
                  onChange={() => setReason(candidate)}
                  disabled={isSubmitting}
                  className="text-admin-lime focus:ring-admin-lime size-4"
                  data-testid={`correction-reason-${candidate}`}
                />
                <span>{t(`admin.receiving.correction.reasons.${candidate}`)}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">
          {t('admin.receiving.correction.reclassifyLabel')}
        </legend>
        <div role="radiogroup" className="grid grid-cols-2 gap-2">
          {(['damaged', 'missing'] as const).map((candidate) => {
            const radioId = `${id}-reclassify-${candidate}`;
            return (
              <label
                key={candidate}
                htmlFor={radioId}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm',
                  reclassifyAs === candidate
                    ? 'border-admin-lime bg-admin-lime-light/40'
                    : 'border-input bg-card',
                )}
              >
                <input
                  id={radioId}
                  type="radio"
                  name={`${id}-reclassify`}
                  value={candidate}
                  checked={reclassifyAs === candidate}
                  onChange={() => setReclassifyAs(candidate)}
                  disabled={isSubmitting}
                  className="text-admin-lime focus:ring-admin-lime size-4"
                  data-testid={`correction-reclassify-${candidate}`}
                />
                <span>{t(`admin.receiving.correction.reclassify.${candidate}`)}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="space-y-1">
        <Label htmlFor={`${id}-note`} className="text-sm">
          {t('admin.receiving.correction.note')}
        </Label>
        <Textarea
          id={`${id}-note`}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          disabled={isSubmitting}
          maxLength={RECEIVING_LIMITS.maxNote}
          aria-invalid={submitted && (noteRequiredMissing || !noteClean.ok) ? true : undefined}
          aria-describedby={submitted && noteRequiredMissing ? `${id}-note-error` : undefined}
          className="text-start"
          data-testid="correction-note"
        />
        {submitted && noteRequiredMissing ? (
          <p id={`${id}-note-error`} className="text-destructive text-xs" role="alert">
            {t('admin.receiving.correction.noteRequired')}
          </p>
        ) : null}
      </div>

      <p className="text-muted-foreground text-xs">
        {t('admin.receiving.correction.increaseHint')}
      </p>

      {hasError ? (
        <p role="alert" className="text-destructive text-sm" data-testid="correction-error">
          {t(receivingErrorKey(error), receivingErrorOptions(error))}
        </p>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          disabled={isSubmitting}
          onClick={() => onOpenChange(false)}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          className="min-h-11"
          disabled={isSubmitting}
          data-testid="correction-submit"
        >
          {isSubmitting ? (
            <Loader2
              aria-hidden="true"
              className="size-4 animate-spin motion-reduce:animate-none"
            />
          ) : null}
          {isSubmitting
            ? t('admin.receiving.correction.applying')
            : hasError
              ? t('common.retry')
              : t('admin.receiving.correction.confirm')}
        </Button>
      </DialogFooter>
    </form>
  );
}
